import { Button } from "@shopify/polaris";
import { IPurchaseStateWithCustomerRelationship } from "@smartrr/shared/entities/PurchaseState";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { useSmartrrVendorDispatch } from "@vendor-app/app/_state/typedVendorReduxHooks";
import { isSavedByVariant } from "../../../libs/utils/isSavedByVariant";
import { addPackageProtection } from "@vendor-app/app/_state/actionCreators/customerPurchaseState";
import { formatMoneyWithoutPrecisionConversion } from "@smartrr/shared/utils/formatMoney";

import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";

interface IPackageProtectionButtonProps {
  customerPurchaseState: IPurchaseStateWithCustomerRelationship;
}

export const PackageProtectionButton = ({ customerPurchaseState }: IPackageProtectionButtonProps) => {
  const { addToast } = useToast();
  const dispatch = useSmartrrVendorDispatch();
  const { savedByIntegration: savedByIntegrationFlag } = useSmartrrFlags();

  const [packageProtectionPrice, setPackageProtectionPrice] = useState<number | null>(null);
  const [isPackageProtectionOffered, setIsPackageProtectionOffered] = useState<boolean | null>(null);
  const [isFetchingPackageProtection, setIsFetchingPackageProtection] = useState(false);
  const [isAddingPackageProtection, setIsAddingPackageProtection] = useState(false);

  const hasPackageProtection = useMemo(() => {
    return customerPurchaseState.stLineItems.some(lineItem => isSavedByVariant(lineItem.vnt));
  }, [customerPurchaseState.stLineItems]);

  const isPrepaid = useMemo(
    () => isCPSPrepaid(customerPurchaseState.schedule),
    [customerPurchaseState.schedule]
  );
  const isSequential = useMemo(
    () => customerPurchaseState.stLineItems.some(lineItem => lineItem.sequential),
    [customerPurchaseState.stLineItems]
  );

  const packageProtectionNotValid = useMemo(
    () =>
      !savedByIntegrationFlag ||
      hasPackageProtection ||
      isPackageProtectionOffered !== true ||
      isPrepaid ||
      isSequential,
    [savedByIntegrationFlag, hasPackageProtection, isPackageProtectionOffered, isPrepaid, isSequential]
  );

  const fetchPackageProtectionPrice = useCallback(async () => {
    if (isFetchingPackageProtection || isPackageProtectionOffered !== null) {
      return;
    }

    setIsFetchingPackageProtection(true);
    try {
      const res = await typedFrontendVendorApi.getReq("/subscriptions/:subscriptionId/package-protection", {
        params: { subscriptionId: customerPurchaseState.id },
      });

      if (res.type === "success" && res.body.isProtectionOffered) {
        setPackageProtectionPrice(res.body.amount);
        setIsPackageProtectionOffered(true);
      } else {
        setIsPackageProtectionOffered(false);
      }
    } finally {
      setIsFetchingPackageProtection(false);
    }
  }, [customerPurchaseState.id, isFetchingPackageProtection, isPackageProtectionOffered]);

  useEffect(() => {
    void fetchPackageProtectionPrice();
  }, [fetchPackageProtectionPrice]);

  const handleAddPackageProtection = useCallback(async () => {
    setIsAddingPackageProtection(true);
    try {
      const res = await dispatch(addPackageProtection(customerPurchaseState.id));
      if (res.type === "ADDED_PACKAGE_PROTECTION") {
        addToast("Package protection added to subscription", false);
      } else if (res.type === "ERROR_ADDING_PACKAGE_PROTECTION") {
        addToast("Failed to add package protection", true);
      }
    } catch {
      addToast("Failed to add package protection", true);
    } finally {
      setIsAddingPackageProtection(false);
    }
  }, [addToast, customerPurchaseState.id, dispatch]);

  if (packageProtectionNotValid) {
    return null;
  }

  return (
    <Button
      id="subscription-details__add-package-protection-button"
      disabled={isAddingPackageProtection}
      loading={isAddingPackageProtection}
      onClick={() => {
        void handleAddPackageProtection();
      }}
    >
      {packageProtectionPrice === null
        ? "Add package protection"
        : `Add package protection for ${formatMoneyWithoutPrecisionConversion(packageProtectionPrice, customerPurchaseState.currency)}`}
    </Button>
  );
};
